import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { PARKING_STATUS } from "@/lib/constants";
import {
  formatDuration,
  getParkingAddress,
  mockParkingSessions,
} from "@/lib/mockData";
import { Calendar, Clock, CreditCard, DollarSign, MapPin } from "lucide-react";
import { useTranslation } from "react-i18next";

function ParkingStatusBadge({ status }: { status: string }) {
  const { t } = useTranslation();

  const getStatusConfig = (status: string) => {
    switch (status) {
      case PARKING_STATUS.ACTIVE:
        return {
          label: t("parkingActive"),
          variant: "default" as const,
          className: "bg-green-500 hover:bg-green-600",
        };
      case PARKING_STATUS.STOPPED:
        return {
          label: t("parkingStopped"),
          variant: "secondary" as const,
          className: "",
        };
      case PARKING_STATUS.PENDING:
        return {
          label: t("parkingPending"),
          variant: "outline" as const,
          className: "border-yellow-500 text-yellow-700",
        };
      default:
        return {
          label: status,
          variant: "outline" as const,
          className: "",
        };
    }
  };

  const config = getStatusConfig(status);

  return (
    <Badge variant={config.variant} className={config.className}>
      {config.label}
    </Badge>
  );
}

function ParkingSessionCard({
  session,
}: {
  session: (typeof mockParkingSessions)[0];
}) {
  const { t } = useTranslation();
  const address = getParkingAddress(session);
  const duration = formatDuration(session.startTime, session.endTime);
  const isActive = session.status === PARKING_STATUS.ACTIVE;

  return (
    <Card className="w-full">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium text-sm">{address}</span>
          </div>
          <ParkingStatusBadge status={session.status} />
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex items-center gap-2 text-sm">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <div>
              <div className="font-medium">{t("startTime")}</div>
              <div className="text-muted-foreground">
                {session.startTime.toLocaleDateString()}{" "}
                {session.startTime.toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </div>
            </div>
          </div>

          {session.endTime && (
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <div>
                <div className="font-medium">{t("endTime")}</div>
                <div className="text-muted-foreground">
                  {session.endTime.toLocaleDateString()}{" "}
                  {session.endTime.toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="flex items-center gap-2 text-sm">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <div>
              <div className="font-medium">{t("duration")}</div>
              <div className="text-muted-foreground">{duration}</div>
            </div>
          </div>

          {session.cost && (
            <div className="flex items-center gap-2 text-sm">
              <DollarSign className="h-4 w-4 text-muted-foreground" />
              <div>
                <div className="font-medium">{t("cost")}</div>
                <div className="text-muted-foreground">
                  {t("currency")}
                  {session.cost.toFixed(2)}
                </div>
              </div>
            </div>
          )}

          {session.paymentMethod && (
            <div className="flex items-center gap-2 text-sm">
              <CreditCard className="h-4 w-4 text-muted-foreground" />
              <div>
                <div className="font-medium">{t("paymentMethod")}</div>
                <div className="text-muted-foreground">
                  {session.paymentMethod}
                </div>
              </div>
            </div>
          )}
        </div>

        {isActive && (
          <div className="mt-3 p-3 bg-green-50 dark:bg-green-950 rounded-lg border border-green-200 dark:border-green-800">
            <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-300">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="font-medium">
                {t("parkingActive")} - {duration}
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function ParkingHistory() {
  const { t } = useTranslation();
  const sessions = mockParkingSessions;
  const activeSessions = sessions.filter(
    (s) => s.status === PARKING_STATUS.ACTIVE,
  );
  const completedSessions = sessions.filter(
    (s) => s.status !== PARKING_STATUS.ACTIVE,
  );
  const totalCost = sessions.reduce(
    (sum, session) => sum + (session.cost || 0),
    0,
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">
          {t("parkingHistory")}
        </h1>
        <p className="text-muted-foreground">
          {t("parkingHistoryDescription")}
        </p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                <MapPin className="h-4 w-4 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {t("parkingActive")}
                </p>
                <p className="text-2xl font-bold">{activeSessions.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <Calendar className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {t("parkingSessions")}
                </p>
                <p className="text-2xl font-bold">{sessions.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                <DollarSign className="h-4 w-4 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {t("totalCost")}
                </p>
                <p className="text-2xl font-bold">
                  {t("currency")}
                  {totalCost.toFixed(2)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Active Sessions */}
      {activeSessions.length > 0 && (
        <div>
          <h2 className="text-xl font-semibold mb-4">Active Parking</h2>
          <div className="space-y-4">
            {activeSessions.map((session) => (
              <ParkingSessionCard key={session.id} session={session} />
            ))}
          </div>
        </div>
      )}

      {/* Recent Sessions */}
      <div>
        <h2 className="text-xl font-semibold mb-4">
          {activeSessions.length > 0 ? "Recent Sessions" : t("parkingSessions")}
        </h2>
        {completedSessions.length > 0 ? (
          <div className="space-y-4">
            {completedSessions.map((session) => (
              <ParkingSessionCard key={session.id} session={session} />
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-8">
                <MapPin className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">{t("noParkingHistory")}</p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
